import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getLevelTitles } from '../data/levelTitles';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, RotateCcw, Dumbbell, User as UserIcon } from 'lucide-react';
import PracticePath, { PracticeItem } from './PracticePath';
import LanguageSelector from './LanguageSelector';
import { LanguageCode} from '../types/types';
import { useTheme } from './ThemeContext';
import GoogleAuth from './GoogleAuth';
import './MainMenu.css';
import Footer from './Footer';
import { useSecureAuth } from '../hooks/useSecureAuth';
import { getDisplayedStreak, getStreakChain } from '../hooks/useDailyStreak';
import { SubscriptionTier } from '../services/subscriptionService';
import { EmojiText, Picto } from './common/Picto';
// ===============================================
// TYPES
// ===============================================
export type AppMode =
  | 'menu'
  | 'learn'
  | 'review'
  | 'quiz'
  | 'sentenceConstruction'
  | 'sentenceGap'
  | 'grammar'
  | 'exercise'
  | 'minigames'
  | 'songAnalyzer'
  | 'wordle'
  | 'memoryDuel'
  | 'forge'
  | 'weaver'
  | 'challenge';

export type MainTab = 'learn' | 'review' | 'games' | 'stats' | 'profile';
export type SubModule =
  | 'vocabulary'
  | 'construction'
  | 'reset'
  | 'grammar'
  | 'revision'
  | 'quiz'
  | 'sentenceGap'
  | 'sentenceConstruction'
  | 'exercises'
  | 'challenge'
  | 'profile'
  | 'minigames-quiz';

// ===============================================
// 🔥 PALIERS DE SÉRIE (streak milestones)
// ===============================================
const STREAK_MILESTONES = [
  { days: 3,   emoji: '𓇌', name: 'Premier élan',   color: 'var(--gold-primary)' },
  { days: 7,   emoji: '𓋹', name: 'Une semaine',     color: '#D4884A' },
  { days: 14,  emoji: '𓂀', name: 'Deux semaines',   color: '#B86030' },
  { days: 30,  emoji: '𓁹', name: 'Un mois',         color: '#A05028' },
  { days: 50,  emoji: '𓃭', name: 'Champion',        color: '#8B4020' },
  { days: 100, emoji: '𓅃', name: 'Légendaire',      color: '#7A3018' },
  { days: 365, emoji: '𓇳', name: 'Maître Absolu',   color: '#E8C080' },
] as const;

// ===============================================
// COULEURS DESIGN SYSTEM
// ===============================================
const COLORS = {
  goldPrimary: 'var(--gold-primary)',
  goldDark: 'var(--gold-dark)',
  goldLight: 'var(--gold-light)',
  surface: 'var(--surface)',
  text: 'var(--text)',
  border: 'var(--border-subtle)',
};


// ===============================================
// NIVEAUX XP - 12 NIVEAUX NOMMÉS + PRESTIGE INFINI
// ===============================================
const LEVELS = [
  { threshold: 0,     required: 250  },   // Niveau 1 -> 2
  { threshold: 250,   required: 500  },   // Niveau 2 -> 3
  { threshold: 750,   required: 750  },   // Niveau 3 -> 4
  { threshold: 1500,  required: 1000 },   // Niveau 4 -> 5
  { threshold: 2500,  required: 1500 },   // Niveau 5 -> 6
  { threshold: 4000,  required: 2000 },   // Niveau 6 -> 7
  { threshold: 6000,  required: 2500 },   // Niveau 7 -> 8
  { threshold: 8500,  required: 3000 },   // Niveau 8 -> 9
  { threshold: 11500, required: 4000 },   // Niveau 9 -> 10
  { threshold: 15500, required: 5000 },   // Niveau 10 -> 11
  { threshold: 20500, required: 7500 },   // Niveau 11 -> 12
  { threshold: 28000, required: 10000 },  // Niveau 12 -> prestige (infini)
];

const XP_PER_ELEMENT = 10;
const PRESTIGE_BASE_XP = 28000;
const PRESTIGE_STEP_XP = 10000;

// ===============================================
// INTERFACES
// ===============================================
interface MainMenuProps {
  onSelectMode: (mode: AppMode) => void;
  reviewDue: boolean;
  /** Nombre exact de mots à réviser calculé par RevisionMode (getDueWordsForCategory).
   *  null = pas encore calculé (RevisionMode n'a pas encore été monté). */
  reviewDueCount?: number | null;
  onResetProgress: () => void;
  learnedWordsCount: number;
  languageCode: LanguageCode;
  onLanguageChange: (code: LanguageCode) => void;
  isMobileView?: boolean;
  revisionProgress?: { wordsToReview: Set<string> };
  onLogin?: (response: any) => void;
  onLogout?: () => void;
  onForceLoginPage?: () => void;
  userProgress?: any;
  completedCategories?: string[];
  onOpenSubscription?: () => void;
  subscriptionTier?: SubscriptionTier;
  refreshTrigger?: number;
  onResetQuotas?: () => void;
  initialTab?: MainTab;
  isFirstVisit?: boolean;
  onSelectMiniGamesMode?: (mode: 'quiz') => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToTerms?: () => void;
  /** Desktop only: content to render in the main area instead of the module grid */
  desktopContent?: React.ReactNode;
  /** Desktop only: current app mode for sidebar active state */
  currentAppMode?: string;
  /** Desktop only: pending challenges count for sidebar badge */
  pendingChallengesCount?: number;
  /** Desktop only: callback to select a top-level tab (mirrors mobile bottom-nav logic) */
  onSelectTab?: (tab: MainTab) => void;
}

// ===============================================
// FONCTION CALCUL XP/NIVEAU AMÉLIORÉE
// ===============================================
const calculateXPLevel = (_wordsCount: number, language: LanguageCode) => {
  const finalXP = parseInt(localStorage.getItem(`${language}-totalXP`) || '0');

  // Prestige infini au-delà du niveau 12
  if (finalXP >= PRESTIGE_BASE_XP) {
    const overflow = finalXP - PRESTIGE_BASE_XP;
    const prestigeSteps = Math.floor(overflow / PRESTIGE_STEP_XP);
    const currentThreshold = PRESTIGE_BASE_XP + prestigeSteps * PRESTIGE_STEP_XP;
    const progressInLevel = finalXP - currentThreshold;
    const progressPercent = Math.round((progressInLevel / PRESTIGE_STEP_XP) * 100);
    return {
      level: 12 + prestigeSteps,
      progress: progressPercent,
      totalXP: finalXP,
      xpForNextLevel: currentThreshold + PRESTIGE_STEP_XP,
      currentLevelXP: currentThreshold,
      remainingXP: PRESTIGE_STEP_XP - progressInLevel,
      totalElements: Math.floor(finalXP / XP_PER_ELEMENT),
      isMaxLevel: false,
      overflowXP: 0,
    };
  }

  // Niveaux 1-12 nommés
  let currentLevel = 1;
  let currentThreshold = 0;
  let requiredXP = LEVELS[0].required;

  for (let i = 0; i < LEVELS.length; i++) {
    if (finalXP >= LEVELS[i].threshold) {
      currentLevel = i + 1;
      currentThreshold = LEVELS[i].threshold;
      requiredXP = LEVELS[i].required;
    } else {
      break;
    }
  }

  const progressInCurrentLevel = finalXP - currentThreshold;
  const progressPercent = Math.min(100, (progressInCurrentLevel / requiredXP) * 100);

  return {
    level: currentLevel,
    progress: progressPercent,
    totalXP: finalXP,
    xpForNextLevel: currentThreshold + requiredXP,
    currentLevelXP: currentThreshold,
    remainingXP: requiredXP - progressInCurrentLevel,
    totalElements: Math.floor(finalXP / XP_PER_ELEMENT),
    isMaxLevel: false,
    overflowXP: 0,
  };
};

// ===============================================
// COMPOSANT PRINCIPAL
// ===============================================
const MainMenu: React.FC<MainMenuProps> = ({
  onSelectMode,
  reviewDue,
  reviewDueCount,

  onResetProgress,
  learnedWordsCount,
  languageCode,
  onForceLoginPage,
  onLanguageChange,
  isMobileView = false,
  revisionProgress = { wordsToReview: new Set<string>() },
  onLogin,
  onLogout,
  onOpenSubscription,
  subscriptionTier = SubscriptionTier.FREE,
  refreshTrigger,
  onResetQuotas,
  initialTab = 'learn',
  onSelectMiniGamesMode,
  onNavigateToPrivacy,
  onNavigateToTerms,
  desktopContent,
  currentAppMode,
  pendingChallengesCount = 0,
  onSelectTab,
}) => {

  // ===============================================
  // HOOKS
  // ===============================================
  const { subscription, checkAccess } = useSecureAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  
  // États
  const [currentSubscriptionTier, setCurrentSubscriptionTier] = useState<SubscriptionTier>(
    subscription?.isPremium ? SubscriptionTier.PREMIUM : SubscriptionTier.FREE
  );
  const [activeTab, setActiveTab] = useState<MainTab>(initialTab);
  const [subModule, setSubModule] = useState<SubModule | null>(null);
  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  const [isThemeSwitching, setIsThemeSwitching] = useState(false);
  const [showStreakPopup, setShowStreakPopup] = useState(false);
  const hasInitializedRef = useRef(false);
  const renderCountRef = useRef(0);
  const [xpVersion, setXpVersion] = useState(0);

  // ===============================================
  // CALCUL XP/NIVEAU
  // ===============================================
  const xpData = useMemo(() => {
    return calculateXPLevel(learnedWordsCount, languageCode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnedWordsCount, languageCode, xpVersion]);

  // Streak journalier — lu depuis localStorage et mis à jour via event pour éviter le décalage useMemo/effect
  // Même règle que useDailyStreak : jour de grâce et boucliers compris
  const readStreak = getDisplayedStreak;
  const [dailyStreak, setDailyStreak] = useState<number>(readStreak);
  useEffect(() => {
    const handler = () => setDailyStreak(readStreak());
    window.addEventListener('streakUpdated', handler);
    setDailyStreak(readStreak());
    return () => window.removeEventListener('streakUpdated', handler);
  }, [learnedWordsCount, refreshTrigger]);

  // Countdown "streak à risque" : affiché après 18h si l'utilisateur n'a pas encore pratiqué aujourd'hui
  const [streakCountdown, setStreakCountdown] = useState<string | null>(null);
  useEffect(() => {
    const compute = () => {
      const now = new Date();
      if (now.getHours() < 18) { setStreakCountdown(null); return; }
      const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      try {
        const history: string[] = JSON.parse(localStorage.getItem('jogolinga-activity-history') || '[]');
        if (history.includes(todayIso)) { setStreakCountdown(null); return; }
      } catch { setStreakCountdown(null); return; }
      const midnight = new Date(now); midnight.setHours(24, 0, 0, 0);
      const diffMs = midnight.getTime() - now.getTime();
      const h = Math.floor(diffMs / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      setStreakCountdown(h > 0 ? `${h}h${m.toString().padStart(2, '0')}` : `${m}min`);
    };
    compute();
    const id = setInterval(compute, 30000);
    window.addEventListener('streakUpdated', compute);
    return () => {
      clearInterval(id);
      window.removeEventListener('streakUpdated', compute);
    };
  }, []);

  // Historique d'activité (3 jours passés + aujourd'hui centré + 3 jours futurs)
  const weekActivity = useMemo(() => {
    try {
      const raw = localStorage.getItem('jogolinga-activity-history');
      const history: string[] = raw ? JSON.parse(raw) : [];
      const FR_DAYS = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];

      const getIso = (offset: number) => {
        // Utiliser setDate() pour éviter les erreurs DST (±1h changement d'heure)
        const d = new Date();
        d.setDate(d.getDate() + offset);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };

      // Ne cocher que les jours de la chaîne de streak actuelle (jours de grâce
      // tolérés). Si dailyStreak vaut 0, aucun jour n'est coché — streak cassé.
      const streakDays = new Set<string>(dailyStreak === 0 ? [] : getStreakChain(history, getIso(0)));

      const days: { date: string; label: string; active: boolean; isToday: boolean; isFuture: boolean }[] = [];
      for (let i = -3; i <= 3; i++) {
        const iso = getIso(i);
        const dLabel = new Date(); dLabel.setDate(dLabel.getDate() + i);
        days.push({ date: iso, label: FR_DAYS[dLabel.getDay()], active: streakDays.has(iso), isToday: i === 0, isFuture: i > 0 });
      }
      return days;
    } catch { return []; }
  }, [learnedWordsCount, refreshTrigger, showStreakPopup, dailyStreak]);

  const langBadges = getLevelTitles(languageCode);
  const currentBadge = langBadges[Math.min(xpData.level - 1, langBadges.length - 1)];
  const nextBadge = xpData.level < langBadges.length ? langBadges[xpData.level] : null;

  // ===============================================
  // MODULES PAR ONGLET
  // ===============================================
  const modules = useMemo(() => ({
    learn: [] as never[],
    review: [] as never[],
    games: [
      {
        id: 'quiz' as SubModule,
        mode: 'quiz' as AppMode,
        label: t('mainMenu.modules.quiz.label'),
        icon: '🧠',
        color: COLORS.goldPrimary,
        premium: false,
        feature: 'quiz_basic',
        description: t('mainMenu.modules.quiz.description')
      },
      {
        id: 'exercises' as SubModule,
        mode: 'exercise' as AppMode,
        label: t('mainMenu.modules.exercises.label'),
        icon: '✍️',
        color: '#f6695cff',
        premium: true,
        feature: 'exercise_unlimited',
        description: t('mainMenu.modules.exercises.description')
      },
    ],
    stats: [] as never[],
    minigames: [
      {
        id: 'minigames-quiz' as SubModule,
        mode: 'minigames' as AppMode,
        label: t('mainMenu.modules.minigames.label'),
        icon: '🎮',
        color: '#b8860b',
        premium: false,
        feature: '',
        description: t('mainMenu.modules.minigames.description')
      },
    ],
    profile: [
      {
        id: 'profile' as SubModule,
        mode: 'profile' as AppMode,
        label: t('mainMenu.modules.profile.label'),
        icon: '👤',
        color: '#7c3aed',
        premium: false,
        feature: '',
        description: t('mainMenu.modules.profile.description')
      }
    ]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentSubscriptionTier, t]);

  // ===============================================
  // EFFETS - Surveillance abonnement

useEffect(() => {

  
  // Utiliser la prop subscriptionTier en priorité
  if (subscriptionTier !== undefined) {
  
    setCurrentSubscriptionTier(subscriptionTier);
  } else if (subscription) {
    const newTier = subscription.isPremium ? SubscriptionTier.PREMIUM : SubscriptionTier.FREE;
  
    setCurrentSubscriptionTier(newTier);
  }
}, [subscription, subscriptionTier]); // ✅ SANS currentSubscriptionTier

 // ✅ BON - Vérifie avant de changer
useEffect(() => {
  const handleSubscriptionUpdate = (event: CustomEvent) => {
  
    
    // Extraire le tier depuis l'événement
    const newTier = event.detail?.tier || 
                   (event.detail?.isPremium ? SubscriptionTier.PREMIUM : SubscriptionTier.FREE);
    
    // 🔒 PROTECTION : Ne changer QUE si différent
    setCurrentSubscriptionTier(prevTier => {
      if (prevTier !== newTier) {
     
        return newTier;
      }
    
      return prevTier;
    });
  };

  window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate as EventListener);
  
  return () => {
    window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate as EventListener);
  };
}, []); // ✅ Pas de dépendances

  // ===============================================
  // EFFETS - XP Update
  // ===============================================
  useEffect(() => {
    const handleXPUpdate = (event: CustomEvent) => {
      const { source } = event.detail;
      // Force le recalcul du badge/niveau dès que le XP localStorage change
      setXpVersion(v => v + 1);
      if (source !== 'google_drive_sync') {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('syncXPToGoogleDrive'));
        }, 2000);
      }
    };
    
    window.addEventListener('xpUpdated', handleXPUpdate as EventListener);
    return () => window.removeEventListener('xpUpdated', handleXPUpdate as EventListener);
  }, []);

  // ===============================================
  // EFFETS - Screen Width
  // ===============================================
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
    
      setScreenWidth(newWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===============================================
  // EFFETS - Initialisation Tab
  // ===============================================
  useEffect(() => {
    if (!hasInitializedRef.current) {
      setActiveTab(initialTab);
      if (modules[initialTab] && modules[initialTab].length > 0) {
        setSubModule(modules[initialTab][0].id);
      } else {
        setSubModule(null);
      }
      hasInitializedRef.current = true;
    } else {
      // Sur desktop, synchronise activeTab quand le mode applicatif force un onglet spécifique
      setActiveTab(initialTab);
    }
  }, [initialTab, modules]);

  // ===============================================
  // EFFETS - Tab Change
  // ===============================================
  useEffect(() => {
  
    
    if (modules[activeTab] && modules[activeTab].length > 0) {
      const defaultModule = modules[activeTab][0].id;
     
      setSubModule(defaultModule);
    } else {
     
      setSubModule(null);
    }
  }, [activeTab, modules]);

  // ===============================================
  // EFFETS - Mobile Display Monitoring
  // ===============================================
  useEffect(() => {
    if (isMobileView && screenWidth > 0) {
   
      
      const forceMobileDisplay = () => {
        const containers = [
          '.mobile-app-container',
          '.mobile-main-content', 
          '.modern-main-container'
        ];
        
        containers.forEach(selector => {
          const element = document.querySelector(selector) as HTMLElement;
          if (element) {
            element.style.cssText = `
              display: block !important;
              width: 100% !important;
              opacity: 1 !important;
              visibility: visible !important;
              position: relative !important;
              z-index: 1 !important;
              transform: none !important;
            `;
          }
        });

        const grid = document.querySelector('.modern-menu-grid') as HTMLElement;
        if (grid) {
          grid.style.cssText = `
            display: grid !important;
            grid-template-columns: ${screenWidth < 360 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'} !important;
            gap: 15px !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          `;
        }
      };

      forceMobileDisplay();
      const intervalId = setInterval(forceMobileDisplay, 100);
      
      return () => clearInterval(intervalId);
    }
  }, [isMobileView, screenWidth]);

  // ===============================================
  // FONCTIONS - Gestion du thème
  // ===============================================
  const handleThemeToggle = () => {
    setIsThemeSwitching(true);
    toggleTheme();
    setTimeout(() => {
      setIsThemeSwitching(false);
    }, 600);
  };

  // ===============================================
  // FONCTIONS - Gestion des onglets
  // ===============================================
  const handleTabChange = useCallback((tab: MainTab) => {
    setActiveTab(tab);
  }, [activeTab]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [activeTab]);

  // ===============================================
  // FONCTIONS - Vérification et sélection module
  // ===============================================
const checkAndSelectModule = async (tabId: MainTab, moduleId: SubModule, module: any) => {

  
  // ✅ NOUVEAU BLOC : Vérifier le tier AVANT tout
  if (currentSubscriptionTier === SubscriptionTier.PREMIUM) {
    onSelectMode(module.mode);
    return;  // ✅ SORTIR ICI POUR LES PREMIUM
  }
  
  // Ce bloc ne s'exécute QUE pour les utilisateurs FREE
  if (module.premium) {
  
    
    try {
      const hasAccess = await checkAccess(module.feature);
     
      
      if (!hasAccess) {
       
        if (onOpenSubscription) {
        
          onOpenSubscription();
          return;
        }
      } else {
      
      }
    } catch (error) {
     
      
     
      
      if (onOpenSubscription) {
        onOpenSubscription();
        return;
      }
    }
  } else {
 
  }
  
 
  onSelectMode(module.mode);
};

  // ===============================================
  // FONCTIONS - Sélection module
  // ===============================================
  const handleModuleSelect = async (
    tabId: MainTab, 
    moduleId: SubModule, 
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
   
    
    if (moduleId === 'reset') {
  
      
      const confirmReset = window.confirm(t('mainMenu.resetConfirm') as string);
      
      if (confirmReset) {
    
        if (onResetProgress) {
          onResetProgress();
        } else {
          
        }
      } else {
      
      }
      
      return;
    }
    
    if (isMobileView) {
   
      
      if (activeTab !== tabId) {
       
        setActiveTab(tabId);
      }
      
      setSubModule(moduleId);
      
      const selectedModule = modules[tabId].find(m => m.id === moduleId);
      if (!selectedModule) {
return;
      }
      
      await checkAndSelectModule(tabId, moduleId, selectedModule);
      return;
    }
    
    if (activeTab !== tabId) {
      
      setActiveTab(tabId);
      
      setTimeout(() => {
        const targetModule = modules[tabId].find(m => m.id === moduleId);
        if (targetModule) {
        
      
          setSubModule(moduleId);
          checkAndSelectModule(tabId, moduleId, targetModule);
        }
      }, 350);
      
      return;
    }
    
    setSubModule(moduleId);
    
    const selectedModule = modules[tabId].find(m => m.id === moduleId);
    if (!selectedModule) {
return;
    }
    
    await checkAndSelectModule(tabId, moduleId, selectedModule);
  };

  // ===============================================
  // VARIANTS ANIMATIONS
  // ===============================================
  const getMenuItemVariants = () => ({
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    }),
    hover: isMobileView ? {} : {
      scale: 1.02,
      y: -4,
      transition: { duration: 0.2 }
    },
    tap: { scale: 0.98 }
  });

  // ===============================================
  // RENDER
  // ===============================================
  renderCountRef.current++;
  const isPremiumActive = currentSubscriptionTier === SubscriptionTier.PREMIUM;

  // ── Icônes SVG africaines pour les cartes modules (blanc sur fond terracotta) ──
  const MODULE_ICONS = {
    vocabulary: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Livre ouvert / parchemin */}
        <path d="M4 6 Q4 4 6 4 L14 4 L14 24 L6 24 Q4 24 4 22 Z"/>
        <path d="M24 6 Q24 4 22 4 L14 4 L14 24 L22 24 Q24 24 24 22 Z"/>
        <line x1="14" y1="4" x2="14" y2="24"/>
        <line x1="7" y1="9" x2="12" y2="9"/>
        <line x1="7" y1="13" x2="12" y2="13"/>
        <line x1="16" y1="9" x2="21" y2="9"/>
        <line x1="16" y1="13" x2="21" y2="13"/>
      </svg>
    ),
    revision: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
        {/* Cercles concentriques bogolan */}
        <circle cx="14" cy="14" r="10"/>
        <circle cx="14" cy="14" r="5.5"/>
        <circle cx="14" cy="14" r="1.8" fill="white" stroke="none"/>
      </svg>
    ),
    quiz: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Œil stylisé africain */}
        <path d="M4 14 Q14 4 24 14 Q14 24 4 14 Z"/>
        <circle cx="14" cy="14" r="4"/>
        <circle cx="14" cy="14" r="1.5" fill="white" stroke="none"/>
      </svg>
    ),
    exercises: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Plume / calame d'écriture */}
        <path d="M22 4 L8 18 L6 22 L10 20 Z"/>
        <path d="M22 4 Q26 8 20 12 L8 18"/>
        <line x1="6" y1="22" x2="14" y2="14"/>
        <line x1="5" y1="24" x2="10" y2="24"/>
      </svg>
    ),
    'minigames-quiz': (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {/* Étoile 4 branches — motif solaire africain */}
        <path d="M14 3 L16.8 11.2 L25 14 L16.8 16.8 L14 25 L11.2 16.8 L3 14 L11.2 11.2 Z"/>
        <circle cx="14" cy="14" r="2.5"/>
      </svg>
    ),
    profile: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
        {/* Masque africain stylisé */}
        <path d="M14 4 Q22 6 22 14 Q22 22 14 24 Q6 22 6 14 Q6 6 14 4Z"/>
        <line x1="14" y1="4" x2="14" y2="24"/>
        <path d="M14 4 Q20 8 20 14 Q20 20 14 24"/>
        <line x1="9" y1="11" x2="13" y2="11"/>
        <line x1="15" y1="11" x2="19" y2="11"/>
      </svg>
    ),
    reset: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 6 A8 8 0 1 1 7.5 20"/>
        <path d="M5 17 L7.5 20 L10 17"/>
      </svg>
    ),
  };


  // ── Contenu partagé : grille de modules ──────────────────────────────────
  const renderModuleGrid = (gridClass: string) => (
    <AnimatePresence>
      <motion.div
        key={activeTab}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={gridClass}
        style={{ position: 'relative', zIndex: 1, overflow: 'visible', width: '100%', display: 'grid' }}
      >
        {(modules[activeTab as keyof typeof modules] || []).map((module, index) => (
          <motion.div
            key={module.id}
            custom={index}
            variants={getMenuItemVariants()}
            whileHover="hover"
            whileTap="tap"
            className={`egyptian-module-card ${module.premium && currentSubscriptionTier === SubscriptionTier.FREE ? 'premium' : ''}`}
            onClick={(e) => handleModuleSelect(activeTab, module.id, e)}
            style={{ position: 'relative', zIndex: 1 }}
          >
            <div className="egyptian-module-icon">
              {MODULE_ICONS[module.id as keyof typeof MODULE_ICONS] ?? <EmojiText text={module.icon} />}
            </div>
            <div className="egyptian-module-info">
              <div className="egyptian-module-name">
                {module.label}
                {module.premium && currentSubscriptionTier === SubscriptionTier.FREE && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', background: 'linear-gradient(135deg, var(--gold-primary), var(--gold-dark))', color: 'var(--surface)', padding: '2px 8px', borderRadius: '6px', fontWeight: '600' }}>{t('mainMenu.premiumBadge')}</span>
                )}
              </div>
              <div className="egyptian-module-status">{module.description}</div>
            </div>
            {module.id === 'vocabulary' && (
              <button className="commencer-button" onClick={(e) => { e.stopPropagation(); handleModuleSelect(activeTab, module.id, e); }}>▶</button>
            )}
            {module.id === 'revision' && reviewDue && (
              <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'linear-gradient(135deg, var(--color-error), var(--color-error-dark))', minWidth: '20px', height: '20px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '0 5px', boxShadow: '0 0 8px rgba(239,68,68,0.5)', animation: 'pulse 2s infinite' }}>
                {(() => {
                  // reviewDueCount === null : RevisionMode pas encore monté → fallback wordsToReview.size
                  // reviewDueCount === number : valeur précise issue de getDueWordsForCategory
                  const count = reviewDueCount !== null && reviewDueCount !== undefined
                    ? reviewDueCount
                    : revisionProgress.wordsToReview.size;
                  return count > 9 ? '9+' : count;
                })()}
              </div>
            )}
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>
  );


  // Onglet Pratiquer : sentier sinueux (maquette "Pratiquer Chemin", 2b)
  const PRACTICE_ITEMS: (PracticeItem & { mode: AppMode })[] = ([
    {
      id: 'quiz', mode: 'quiz' as AppMode, group: 'solo', label: t('mainMenu.modules.quiz.label'),
      bg: '#C67B4E', ic: '#FFF8EC', icon: 'M4 5H20V16H10L5 20V16H4Z M10 9.5A2 2 0 1 1 12 11.5V12.5 M12 14.6V14.8',
    },
    {
      id: 'exercises', mode: 'exercise' as AppMode, group: 'solo', label: t('mainMenu.modules.exercises.label'),
      bg: '#A4553A', ic: '#FFF8EC', icon: 'M4 20L5 15L16 4L20 8L9 19Z M14 6L18 10',
      locked: currentSubscriptionTier === SubscriptionTier.FREE,
    },
    {
      id: 'forge', mode: 'forge' as AppMode, group: 'solo', label: t('miniGames.forgeCard', 'La forge des mots'),
      bg: '#2A1A12', ic: '#F6B04A', icon: 'M12 22C8 22 5.5 19.4 5.5 16C5.5 12 9 10 9.5 5.5C12 7 13 9.5 12.6 12C14 11.2 14.8 9.8 15 8.5C17.2 10.5 18.5 13 18.5 16C18.5 19.4 16 22 12 22Z',
      locked: currentSubscriptionTier === SubscriptionTier.FREE,
    },
    {
      id: 'weaver', mode: 'weaver' as AppMode, group: 'solo', label: t('weaver.title', 'Le tisserand'),
      bg: '#5E6B3A', ic: '#FFF8EC', icon: 'M4 6H20 M4 12H20 M4 18H20 M8 3V21 M16 3V21',
      locked: currentSubscriptionTier === SubscriptionTier.FREE,
    },
    {
      id: 'culturalQuiz', mode: 'minigames' as AppMode, group: 'solo', label: t('mainMenu.gamesCards.culturalQuiz', 'Quiz culturel'),
      bg: '#8A6D3B', ic: '#FFF8EC', icon: 'M12 3A9 9 0 1 0 12 21A9 9 0 1 0 12 3Z M3 12H21 M12 3C9 6 9 18 12 21 M12 3C15 6 15 18 12 21',
    },
    {
      id: 'songAnalyzer', mode: 'songAnalyzer' as AppMode, group: 'solo', label: t('miniGames.songs', 'Chansons'),
      bg: '#B8862F', ic: '#FFF8EC', icon: 'M9 18V5L20 3V16 M9 18A3 3 0 1 1 3 18A3 3 0 1 1 9 18Z M20 16A3 3 0 1 1 14 16A3 3 0 1 1 20 16Z',
    },
    {
      id: 'challenge', mode: 'challenge' as AppMode, group: 'multi', label: t('miniGames.challengeFriend', 'Défier un ami'),
      bg: '#A4553A', ic: '#FFF8EC', icon: 'M4 4L15 15M20 4L9 15M13 17L17 13M7 13L11 17M15 15L19 19M9 15L5 19',
      badge: pendingChallengesCount,
    },
  ] as (PracticeItem & { mode: AppMode })[]).filter(item => !(languageCode === 'ka' && (item.id === 'forge' || item.id === 'weaver')));

  const renderGamesGrid = () => {
    const handleSelect = (item: PracticeItem) => {
      const full = PRACTICE_ITEMS.find(x => x.id === item.id);
      if (!full) return;
      if (full.locked) {
        if (onOpenSubscription) onOpenSubscription();
        return;
      }
      onSelectMode(full.mode);
    };

    return (
      <div style={{ paddingTop: '88px', paddingBottom: '24px', background: 'transparent', minHeight: !isMobileView ? '100%' : undefined }}>
        <PracticePath items={PRACTICE_ITEMS} onSelect={handleSelect} />
      </div>
    );
  };

  const sectionTitle = (
    <h2 className="egyptian-section-title">
      {activeTab === 'learn' && t('mainMenu.sectionTitles.learn')}
      {activeTab === 'review' && t('mainMenu.sectionTitles.review')}
      {activeTab === 'games' && t('mainMenu.sectionTitles.games')}
      {activeTab === 'profile' && t('mainMenu.modules.profile.label')}
    </h2>
  );

  return (
    <div
      className={`modern-main-container egyptian-style${!isMobileView ? ' desktop-layout' : ''}${isMobileView && activeTab === 'games' ? ' games-fullscreen' : ''}`}
      style={{ paddingBottom: isMobileView ? '120px' : '0px', position: 'relative', zIndex: 1 }}
    >
      {!isMobileView ? (
        /* ================================================
           DESKTOP SIDEBAR LAYOUT
           ================================================ */
        <div className="desktop-sidebar-layout">

          {/* ── Sidebar fixe gauche ── */}
          <aside className="desktop-sidebar">
            <div className="sidebar-logo">
              <div className="sidebar-logo-glyph"><img src="/assets/logo-mark.svg" alt="" /></div>
              <span className="sidebar-brand-name">JogoLinga</span>
            </div>

            <nav className="sidebar-nav">
              {([
                // Mêmes icônes que la bottom nav mobile (App.tsx) : hiéroglyphes
                // pour Copte/Kama (thème égyptien), icônes au trait sinon.
                { id: 'learn',   glyph: '𓀁', Icon: BookOpen,  label: t('mainMenu.sidebar.learn'),   mode: 'learn'   },
                { id: 'review',  glyph: '𓇳', Icon: RotateCcw, label: t('mainMenu.sidebar.review'),  mode: 'review'  },
                { id: 'games',   glyph: '𓊃', Icon: Dumbbell,  label: t('mainMenu.sidebar.games'),   mode: null      },
                { id: 'profile', glyph: '𓀀', Icon: UserIcon,  label: t('mainMenu.sidebar.profile'), mode: 'profile' },
              ] as { id: MainTab; glyph: string; Icon: typeof BookOpen; label: string; mode: AppMode | null }[]).map(({ id, glyph, Icon, label, mode }) => {
                const isEgyptianTheme = languageCode === 'co' || languageCode === 'ka';
                const modeToTab: Record<string, MainTab> = {
                  learn: 'learn', grammar: 'learn', sentenceConstruction: 'learn',
                  review: 'review',
                  quiz: 'games', exercise: 'games', sentenceGap: 'games',
                  minigames: 'games', challenge: 'games', dialogue: 'games',
                  profile: 'profile',
                };
                const effectiveTab = currentAppMode && currentAppMode !== 'menu'
                  ? (modeToTab[currentAppMode] ?? activeTab)
                  : activeTab;
                const isActive = effectiveTab === id;
                return (
                  <button
                    key={id}
                    className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                    style={{ position: 'relative' }}
                    onClick={() => {
                      if (id === 'games') { handleTabChange('games'); onSelectTab ? onSelectTab('games') : onSelectMode('menu' as AppMode); }
                      else if (onSelectTab && id !== 'profile') { onSelectTab(id); }
                      else { mode ? onSelectMode(mode) : onSelectMode('menu' as AppMode); }
                    }}
                  >
                    <span className="sidebar-nav-icon">{isEgyptianTheme ? glyph : <Icon size={22} strokeWidth={1.75} />}</span>
                    <span className="sidebar-nav-label">{label}</span>
                    {id === 'review' && reviewDue && (
                      <div style={{ position: 'absolute', top: '6px', right: '8px', background: 'linear-gradient(135deg,var(--color-error), var(--color-error-dark))', minWidth: '16px', height: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '0 3px' }}>
                        {(reviewDueCount != null && reviewDueCount > 9) ? '9+' : (reviewDueCount ?? '!')}
                      </div>
                    )}
                    {id === 'games' && pendingChallengesCount > 0 && (
                      <div style={{ position: 'absolute', top: '6px', right: '8px', background: 'linear-gradient(135deg,var(--color-error), var(--color-error-dark))', minWidth: '16px', height: '16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '0 3px' }}>
                        {pendingChallengesCount > 9 ? '9+' : pendingChallengesCount}
                      </div>
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="sidebar-footer">
              <motion.div
                className={`desktop-sub-card${isPremiumActive ? ' active' : ''}`}
                onClick={onOpenSubscription}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="desktop-sub-icon"><EmojiText text={isPremiumActive ? '👑' : '🆓'} /></span>
                <div className="desktop-sub-info">
                  <div className="desktop-sub-title">{isPremiumActive ? t('mainMenu.sidebar.premiumActive') : t('mainMenu.sidebar.free')}</div>
                  <div className="desktop-sub-subtitle">{isPremiumActive ? t('mainMenu.sidebar.unlimitedAccess') : t('mainMenu.sidebar.goPremium')}</div>
                </div>
              </motion.div>
            </div>
          </aside>

          {/* ── Zone de contenu principale ── */}
          <div className="desktop-content">
            <div className="desktop-topbar">
              {/* Gauche : sélecteur de langue */}
              <div className="topbar-left">
                <LanguageSelector value={languageCode} onChange={onLanguageChange} />
              </div>

              {/* Centre : niveau + XP + streak */}
              <div className="topbar-level">
                <div className="topbar-level-info">
                  <div className="topbar-level-name">
                    {t('header.level', { level: xpData.level })} · {currentBadge.name}
                  </div>
                  <div className="topbar-xp-track">
                    <div className="topbar-xp-fill" style={{ width: `${xpData.progress}%` }} />
                  </div>
                  <div className="topbar-xp-text">
                    {xpData.totalXP}/{xpData.xpForNextLevel} XP · {Math.round(xpData.progress)}%
                  </div>
                </div>
                <div style={{ position: 'relative' }}>
                    <button
                      className="egyptian-streak-badge"
                      onClick={() => setShowStreakPopup(v => !v)}
                      aria-label={t('mainMenu.streak.streakAriaLabel', { count: dailyStreak }) as string}
                      style={dailyStreak === 0 ? { opacity: 0.4, filter: 'grayscale(1)' } : undefined}
                    >
                      <span className="streak-fire"><Picto name="feu" /></span>
                      <span className="streak-count-label">{dailyStreak}</span>
                      {streakCountdown && (
                        <span style={{
                          fontSize: '10px',
                          fontWeight: 700,
                          background: theme === 'dark' ? 'rgba(239,68,68,0.18)' : 'rgba(242,230,203,0.25)',
                          color: theme === 'dark' ? 'var(--color-error)' : '#F2E6CB',
                          padding: '1px 6px',
                          borderRadius: '8px',
                          marginLeft: '4px',
                          animation: 'pulse 2s infinite',
                        }}>
                          ⏱ {streakCountdown}
                        </span>
                      )}
                    </button>
                    {showStreakPopup && (
                      <>
                        <div className="streak-popup-overlay" onClick={() => setShowStreakPopup(false)} />
                        <div className="streak-popup">
                          <div className="streak-popup-title">
                            <EmojiText text={t('mainMenu.streak.currentTitle') as string} /> <strong>{t('mainMenu.streak.streakDays', { count: dailyStreak })}</strong>
                          </div>
                          <div className="streak-week-grid">
                            {weekActivity.map(day => (
                              <div key={day.date} className={`streak-day ${day.active ? 'active' : 'inactive'} ${day.isToday ? 'today' : ''} ${day.isFuture ? 'future' : ''}`}>
                                <div className="streak-day-circle">
                                  {day.active && !day.isFuture && <span className="streak-day-check">✓</span>}
                                </div>
                                <div className="streak-day-label">{day.label}</div>
                              </div>
                            ))}
                          </div>
                          {!weekActivity.find(d => d.isToday && d.active) && (
                            <div className="streak-popup-hint"><EmojiText text={t('mainMenu.streak.hint') as string} /></div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
              </div>

              {/* Droite : thème + connexion */}
              <div className="topbar-right">
                <button
                  className={`egyptian-theme-button ${isThemeSwitching ? 'switching' : ''} ${theme === 'dark' ? 'dark-mode' : 'light-mode'}`}
                  onClick={handleThemeToggle}
                  title={(theme === 'dark' ? t('mainMenu.switchToLight') : t('mainMenu.switchToDark')) as string}
                >
                  <EmojiText text={theme === 'dark' ? '☀️' : '🌙'} />
                </button>
                <GoogleAuth onLogin={onLogin!} onLogout={onLogout} isHeader={true} />
              </div>
            </div>

            <div className="desktop-main-content">
              {desktopContent ? desktopContent :
                activeTab === 'games' ? renderGamesGrid() : (
                  <div className="desktop-default-grid-wrapper">
                    {sectionTitle}
                    {renderModuleGrid('desktop-modules-grid')}
                  </div>
                )
              }
              {currentAppMode !== 'profile' && (
                <Footer onNavigateToPrivacy={onNavigateToPrivacy} onNavigateToTerms={onNavigateToTerms} />
              )}
            </div>
          </div>

        </div>
      ) : (
        /* ================================================
           MOBILE LAYOUT (inchangé)
           ================================================ */
        <>
          {activeTab !== 'games' && sectionTitle}
          {activeTab === 'games' ? renderGamesGrid() : renderModuleGrid('modern-menu-grid')}
        </>
      )}

      <style>{`
        @keyframes golden-shimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
};

export default MainMenu;
